import {
    isLongtaskPerformanceEntry,
    isResourcePerformanceEntry,
    patchFetch,
    patchXMLHTTPRequest,
} from './TimeToInteractiveTrackerHelpers';

const ALLOWED_LONG_TASKS_GAP = 5;
const CHECK_RETRY_DELAY = 1000;
const QUIET_REQUESTS_COUNT = 0;
export const QUIET_WINDOW_DURATION = 2000;

interface StoredPerformanceEntry {
    start: number;
    end: number;
}

interface TimeToInteractiveTrackerOptions {
    onTrackEnd(name: string, timeToInteractive: number): void;
}

/**
 * Трекер Time To Interactive (TTI).
 *
 * Использует алгоритм "Consistently Interactive". Значение TTI равно времени
 * от начала замера до конца последнего long task, а если long tasks не было, оно равно 0.
 *
 * Замер завершается, когда QUIET_WINDOW_DURATION подряд одновременно выполняются два условия:
 * 1. Нет long tasks (CPU idle)
 * 2. Нет более QUIET_REQUESTS_COUNT незавершённых сетевых запросов (Network idle)
 *
 * Сетевые запросы только откладывают завершение замера и в значение не входят.
 *
 * @kind helper
 */
export class TimeToInteractiveTracker {
    private options: TimeToInteractiveTrackerOptions;

    private trackFrom: number = 0;
    private trackName: string = '';

    private timerId: number | undefined = undefined;
    private timerActivationTime: number = -Infinity;

    private longTasks: StoredPerformanceEntry[] = [];
    private networkRequests: StoredPerformanceEntry[] = [];
    private incompleteJSInitiatedRequestStartTimes = new Map<number, number>();

    private performanceObserver = new PerformanceObserver(this.performanceObserverCallback.bind(this));

    constructor(options: TimeToInteractiveTrackerOptions) {
        this.options = options;

        // Используем `buffered: true`, чтобы получить long tasks и сетевые запросы,
        // которые произошли до создания наблюдателя (например, во время начальной
        // загрузки MPA-страницы, ещё до инициализации трекера).
        this.performanceObserver.observe({ type: 'longtask', buffered: true });
        this.performanceObserver.observe({ type: 'resource', buffered: true });

        patchXMLHTTPRequest(
            this.beforeJSInitiatedRequestCallback.bind(this),
            this.afterJSInitiatedRequestCallback.bind(this)
        );
        patchFetch(this.beforeJSInitiatedRequestCallback.bind(this), this.afterJSInitiatedRequestCallback.bind(this));
    }

    private beforeJSInitiatedRequestCallback(requestId: number) {
        this.incompleteJSInitiatedRequestStartTimes.set(requestId, performance.now());
    }

    private afterJSInitiatedRequestCallback(requestId: number) {
        this.incompleteJSInitiatedRequestStartTimes.delete(requestId);
    }

    private performanceObserverCallback(entryList: PerformanceObserverEntryList) {
        const entries = entryList.getEntries();

        for (const entry of entries) {
            if (isLongtaskPerformanceEntry(entry)) {
                this.longTaskFinishedCallback(entry);
            }

            if (isResourcePerformanceEntry(entry)) {
                this.networkRequestFinishedCallback(entry);
            }
        }
    }

    private longTaskFinishedCallback(performanceEntry: PerformanceEntry) {
        const taskEndTime = performanceEntry.startTime + performanceEntry.duration;

        this.longTasks.push({
            start: performanceEntry.startTime,
            end: taskEndTime,
        });

        this.rescheduleTimer(taskEndTime + QUIET_WINDOW_DURATION);
    }

    private networkRequestFinishedCallback(performanceEntry: PerformanceResourceTiming) {
        this.networkRequests.push({
            start: performanceEntry.fetchStart,
            end: performanceEntry.responseEnd,
        });

        const lastNetworkBusy = this.getLastNetworkBusy(this.incompleteRequestStarts, this.networkRequests);

        this.rescheduleTimer(lastNetworkBusy + QUIET_WINDOW_DURATION);
    }

    /**
     * Возвращает timestamp последней сетевой активности.
     * Сетевой активностью считается выполнение больше QUIET_REQUESTS_COUNT запросов.
     *
     * @param incompleteRequestStarts список timestamp незавершенных запросов
     * @param networkRequests список всех запросов
     *
     * @returns timestamp последней активности, либо 0 если активности не было
     *
     * @internal public только из-за тестов
     */
    public getLastNetworkBusy(incompleteRequestStarts: number[], networkRequests: StoredPerformanceEntry[]) {
        // Если количество незавершенных запросов больше чем разрешенное, то отдаем текущее время
        if (incompleteRequestStarts.length > QUIET_REQUESTS_COUNT) {
            return performance.now();
        }

        const endpoints = [];

        for (const req of networkRequests) {
            endpoints.push({
                timestamp: req.start,
                type: 'requestStart',
            });

            endpoints.push({
                timestamp: req.end,
                type: 'requestEnd',
            });
        }

        for (const ts of incompleteRequestStarts) {
            endpoints.push({
                timestamp: ts,
                type: 'requestStart',
            });
        }

        endpoints.sort((a, b) => a.timestamp - b.timestamp);

        let currentActive = incompleteRequestStarts.length;

        for (let i = endpoints.length - 1; i >= 0; i--) {
            const endpoint = endpoints[i];

            switch (endpoint?.type) {
                case 'requestStart':
                    currentActive--;

                    break;

                case 'requestEnd':
                    currentActive++;

                    // Если когда-то было больше чем разрешенное количество запросов, то отдаем последнее такое время
                    if (currentActive > QUIET_REQUESTS_COUNT) {
                        return endpoint.timestamp;
                    }

                    break;
            }
        }

        // Запросов не было вообще
        return 0;
    }

    private rescheduleTimer(resheduleTime: number) {
        // Если нет trackName, то засекать TTI нас не просили
        if (!this.trackName) {
            return;
        }

        // Уже есть таймер позже чем rescheduleTime, нет смысла его переносить
        if (this.timerActivationTime > resheduleTime) {
            return;
        }

        window.clearTimeout(this.timerId);

        this.timerId = window.setTimeout(() => {
            this.checkTimeToInteractive();
        }, resheduleTime - performance.now());

        this.timerActivationTime = resheduleTime;
    }

    private checkTimeToInteractive() {
        const lastNetworkBusy = this.getLastNetworkBusy(this.incompleteRequestStarts, this.networkRequests);
        const maybeConsistentlyInteractiveTime = this.getConsistentlyInteractiveTime(
            this.trackFrom,
            lastNetworkBusy,
            this.longTasks
        );

        if (maybeConsistentlyInteractiveTime !== null) {
            const timeToInteractive = maybeConsistentlyInteractiveTime - this.trackFrom;

            this.options.onTrackEnd(this.trackName, timeToInteractive);
            this.resetTracking();

            return;
        }

        // Если работа еще не закончилась, то пробуем проверить попозже
        this.rescheduleTimer(performance.now() + CHECK_RETRY_DELAY);
    }

    /**
     * Возвращает timestamp последнего long task
     *
     * @param trackFrom timestamp начала трека
     * @param lastNetworkBusy timestamp последней сетевой активности
     * @param longTasks список long task
     *
     * @returns timestamp последнего long task или null, если long task не было
     *
     * @internal public только из-за тестов
     */
    public getConsistentlyInteractiveTime(
        trackFrom: number,
        lastNetworkBusy: number,
        longTasks: StoredPerformanceEntry[]
    ) {
        const currentTime = performance.now();

        // Если в течение QUIET_WINDOW_DURATION было > QUIET_REQUESTS_COUNT запросов,
        // то считаем что работа после действия пользователя еще не закончилась
        if (currentTime - lastNetworkBusy < QUIET_WINDOW_DURATION) {
            return null;
        }

        const longTasksSinceTrackFrom = longTasks.filter(
            // Небольшой люфт нужен, чтобы учесть и те лонгтаски, которые начались сразу же (в том же тике)
            // после вызова track. Время их начала будет заведомо до trackFrom
            longTask => longTask.start > trackFrom - ALLOWED_LONG_TASKS_GAP
        );
        const maybeConsistentlyInteractiveTime =
            longTasksSinceTrackFrom[longTasksSinceTrackFrom.length - 1]?.end ?? trackFrom;

        // Если в течение QUIET_WINDOW_DURATION был хотя бы один лонгтаск,
        // то считаем что работа после действия пользователя еще не закончилась
        if (currentTime - maybeConsistentlyInteractiveTime < QUIET_WINDOW_DURATION) {
            return null;
        }

        return maybeConsistentlyInteractiveTime;
    }

    /**
     * Сбрасывает текущее отслеживание, не отключая PerformanceObserver и патчи XHR/fetch.
     * Используется при SPA переходах и при скрытии страницы.
     */
    private resetTracking() {
        window.clearTimeout(this.timerId);

        this.trackFrom = 0;
        this.trackName = '';

        this.timerId = undefined;
        this.timerActivationTime = -Infinity;

        this.longTasks = [];
        this.networkRequests = [];
        this.incompleteJSInitiatedRequestStartTimes.clear();
    }

    private get incompleteRequestStarts() {
        return [...this.incompleteJSInitiatedRequestStartTimes.values()];
    }

    /**
     * Начинает отслеживание TTI.
     *
     * @param name - имя метрики
     * @param trackFrom - время начала отслеживания (по умолчанию performance.now())
     */
    public track(name: string, trackFrom?: number) {
        // Сохраняем незавершённые запросы до сброса: они начались до registerSubPage,
        // но ещё выполняются — новая страница, вероятно, зависит от их ответов.
        const carriedOverRequests = new Map(this.incompleteJSInitiatedRequestStartTimes);

        this.resetTracking();

        this.trackName = name;
        this.trackFrom = trackFrom ?? performance.now();

        for (const [id, startTime] of carriedOverRequests) {
            this.incompleteJSInitiatedRequestStartTimes.set(id, startTime);
        }

        this.rescheduleTimer(Math.max(this.trackFrom, performance.now()) + QUIET_WINDOW_DURATION);
    }

    /**
     * Отменяет текущее отслеживание TTI без уничтожения трекера.
     * PerformanceObserver и патчи XHR/fetch остаются активными.
     */
    public cancel() {
        this.resetTracking();
    }

    /**
     * Полностью уничтожает трекер: отменяет отслеживание и отключает PerformanceObserver.
     */
    public destroy() {
        this.resetTracking();
        this.performanceObserver.disconnect();
    }
}
